import { ISupplier } from '@bridebook/models/source/models/Suppliers.types';
import { IUISupplier } from '@bridebook/toolbox/src/types';
import { getVariant_LIVE_27666_EnquiryFormImprovements } from '@bridebook/web/lib/ab-tests/tests/global/LIVE-27666_EnquiryFormImprovements';
import { getScreenType } from '@bridebook/web/lib/app/selectors';
import { EnquiryFormInfoProps, IApplicationState } from '@bridebook/web/lib/types';
import { getSupplierTargetingBudget, isBudgetMatch } from '@bridebook/toolbox/src/isBudgetMatch';
import { EnquiryIntents } from '@bridebook/web/lib/enquiries/intent';

interface IEnquiryAnalyticProperties {
  contactMessage: string;
  contactMethod: string;
  contactLocation: string;
  contactActionLabel: string;
  contactLocationType: string;
  contactIntent: EnquiryIntents;
  contactGalleryCTA?: number | 'end';
  budgetMatch?: boolean;
  inbox?: boolean;
  supplierTier?: number;
  contactType: 'multipleEnquiry' | 'singleEnquiry';
  contactSection?: string;
  cta?: string;
  numberOfActiveShowroundSlots?: number;
  numberOfActiveOpenDays?: number;
}

const getEnquiriesAnalyticsProps = ({
  getState,
  supplier,
  infoProps = {},
  message,
  intent,
}: {
  getState: () => IApplicationState;
  supplier?: IUISupplier | ISupplier | null;
  infoProps?: EnquiryFormInfoProps;
  message?: string;
  intent?: EnquiryIntents;
}): IEnquiryAnalyticProperties => {
  const {
    locationType = 'main',
    actionLabel = '',
    method = '',
    contactGalleryCTA,
    cta,
    contactSection,
    contactLocation = '',
    numberOfActiveShowroundSlots,
    numberOfActiveOpenDays,
  } = infoProps;
  const state = getState();
  const {
    enquiries: { multipleEnquiry, intent: reduxIntent },
    supplier: { premium },
    weddings: {
      profile: { budget },
    },
  } = state;

  // LIVE-27666 — the mobile enquiry form variant reports every contact event as the full contact
  // form. Derived here (the single point mapping `infoProps.method` → `contactMethod`), so both the
  // server-side enquiry events (Triggered / Closed / Emailed) and the "Clicked to …" supplier
  // events are covered without mutating enquiry `infoProps.method` (which drives logic such as the
  // linked-supplier confirmation popup). Multi-enquiry keeps its own contact method.
  const isLIVE_27666VariantActive =
    getVariant_LIVE_27666_EnquiryFormImprovements(state) === '1' &&
    getScreenType(state).isMobile &&
    !multipleEnquiry;

  const isSupplierProps = supplier && !multipleEnquiry;

  const supplierBudgetMatchData =
    isSupplierProps && supplier && 'targetingBudget' in supplier
      ? {
          targeting: getSupplierTargetingBudget(supplier),
          tier: supplier.tier,
          type: supplier.type,
          countryCode: supplier.countryCode,
        }
      : isSupplierProps && supplier && 'targeting' in supplier
        ? {
            targeting: getSupplierTargetingBudget(supplier),
            tier: premium?.tier,
            type: supplier.type[0],
            countryCode: supplier.l10n.country,
          }
        : undefined;

  const contactIntent = intent ?? reduxIntent;

  return {
    contactMessage: message ?? '',
    contactMethod: isLIVE_27666VariantActive ? 'fullContactForm' : method,
    // several CTAs stamp only `method` — fall back to it so the event never reports ''
    contactLocation: contactLocation || method,
    contactSection,
    contactLocationType: locationType,
    contactActionLabel: actionLabel,
    cta,
    contactIntent,
    budgetMatch: supplierBudgetMatchData
      ? isBudgetMatch(budget, supplierBudgetMatchData)
      : undefined,
    contactGalleryCTA,
    contactType: multipleEnquiry ? 'multipleEnquiry' : 'singleEnquiry',
    numberOfActiveShowroundSlots,
    numberOfActiveOpenDays,
  };
};

export default getEnquiriesAnalyticsProps;
